import { Dialog, DialogContent, DialogTitle } from "@/components/shadcn/dialog";
import { Button } from "@/components/shadcn/button";
import { useSetAtom } from "jotai";
import { isTutorialStepAtom } from "../Spotflow/SideNav/sidenavShared";
import {
  Ticket,
  PenLine,
  Headphones,
  ScanLine,
  FileText,
  Users,
  ArrowRight,
} from "lucide-react";
import { Card, CardContent } from "@/components/shadcn/card";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";
import ImageWithFallback from "@/components/ImageWithFallback";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";

interface WelcomeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type OptionId =
  | "ticket-triage"
  | "complaints"
  | "it-service"
  | "doc-classification"
  | "financial"
  | "employee-helpdesk";

interface Option {
  id: OptionId;
  title: string;
  description: string;
  icon: React.ElementType;
  route: string;
  color: string;
  image?: string;
}

const options: Option[] = [
  {
    id: "complaints",
    title: "Drowning in incoming emails and tickets?",
    description:
      "Automatically sort, prioritize, and route each request to the right team so nothing gets missed.",
    icon: PenLine,
    route: "/templates/b2c3d4e5-f678-90a1-b2c3-d4e5f6789012",
    color: "bg-purple-50",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/icons/complaint.svg",
  },
  {
    id: "doc-classification",
    title: "Spending too much time processing invoices?",
    description:
      "Automatically extract and structure data from PDFs, emails, and invoices into your ERP system.",
    icon: ScanLine,
    route: "/templates/d4e5f678-90a1-b2c3-d4e5-f67890123456",
    color: "bg-pink-50",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/icons/documentclassification.svg",
  },
  {
    id: "ticket-triage",
    title: "Too many inquiries slipping through the cracks?",
    description:
      "Organize, prioritize, and draft responses to keep every customer acknowledged.",
    icon: Ticket,
    route: "/templates/a1b2c3d4-e5f6-7890-a1b2-c3d4e5f67890",
    color: "bg-red-50",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/icons/tickettriage.svg",
  },
  {
    id: "financial",
    title: "Is your team overwhelmed by booking requests?",
    description:
      "Manage routine changes, cancellations, and billing FAQs automatically.",
    icon: FileText,
    route: "/templates/78901234-d4e5-f678-9012-3456789abcde",
    color: "bg-slate-50",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/icons/financialdocument.svg",
  },
  {
    id: "it-service",
    title: "Is your shared service team buried in requests?",
    description:
      "Handle account creation, password resets, and troubleshooting automatically.",
    icon: Headphones,
    route: "/templates/c3d4e5f6-7890-a1b2-c3d4-e5f678901234",
    color: "bg-blue-50",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/icons/itservice.svg",
  },
  {
    id: "employee-helpdesk",
    title: "Are your insurance claims being processed manually?",
    description:
      "Automate the claims process, from documents to decision, for faster and cheaper resolution.",
    icon: Users,
    route: "/templates/a7b8c9d0-e1f2-3456-7890-abcdef123456",
    color: "bg-emerald-50",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/icons/employeeselfservice.svg",
  },
];

export const WelcomeModal = ({ open, onOpenChange }: WelcomeModalProps) => {
  const setIsTutorialStep = useSetAtom(isTutorialStepAtom);
  const router = useRouter();

  const handleSelect = (option: Option) => {
    analytics_track(ANALYTICS_EVENTS.ONBOARDING_TEMPLATE_CARD_CLICK, {
      template_id: option.id,
    });
    onOpenChange(false);
    router.push(option.route);
  };

  const handleExplore = () => {
    analytics_track(ANALYTICS_EVENTS.ONBOARDING_DIALOG_CLOSE);
    onOpenChange(false);
    setIsTutorialStep(1);
  };

  return (
    <Dialog open={open} onOpenChange={handleExplore}>
      <DialogContent
        size="6xl"
        className="h-auto max-h-[95%] p-0 bg-background border-none shadow-2xl flex flex-col overflow-y-auto"
        showCloseButton={true}
      >
        <DialogTitle className="sr-only">
          Welcome to Noxus - Tutorial
        </DialogTitle>
        <div className="flex flex-col w-full h-full py-8">
          {/* Header */}
          <div className="text-center px-8">
            <h2 className="text-xl font-semibold text-foreground mb-2 tracking-tight">
              What&apos;s the first problem you&apos;d like to solve?
            </h2>
            <p className="text-muted-foreground text-sm max-w-2xl mx-auto">
              Don&apos;t worry if multiple options catch your eye. You&apos;ll
              get to explore them all later.
            </p>
          </div>

          {/* Content - Scrollable Grid */}
          <div className="flex-1 overflow-y-auto px-8 py-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {options.map((option) => (
                <Card
                  key={option.id}
                  className="hover:shadow-md  transition-all duration-300 cursor-pointer group overflow-hidden border border-border p-4 min-h-50"
                  onClick={() => handleSelect(option)}
                >
                  <CardContent className="p-0 flex flex-col h-full gap-4">
                    {/* Large centered image area */}
                    <div
                      className={cn(
                        "h-24 flex items-center justify-center rounded-sm border border-border/50 overflow-hidden relative",
                        option.color
                      )}
                    >
                      {option.image ? (
                        <div className="relative w-16 h-16 transition-transform duration-300 group-hover:scale-110">
                          <ImageWithFallback
                            src={option.image}
                            alt={option.title}
                            fill
                            className="object-contain"
                          />
                        </div>
                      ) : (
                        <option.icon
                          strokeWidth={1.5}
                          size={48}
                          className="text-foreground/80 transition-transform duration-300 group-hover:scale-110"
                        />
                      )}
                    </div>

                    {/* Content area */}
                    <div className="flex flex-col flex-grow justify-between">
                      <div>
                        <h3 className="font-medium text-sm mb-2 text-primary leading-tight">
                          {option.title}
                        </h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {option.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end px-8">
            <Button
              variant="default"
              onClick={handleExplore}
              className="font-medium gap-2 pl-6 pr-6 bg-foreground text-background hover:bg-foreground/90"
            >
              I just want to explore
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
